import { useEffect, useMemo, useState } from "react";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import Typography from "@mui/material/Typography";
import AccordionDetails from "@mui/material/AccordionDetails";
import { GetSavedSearchResponse, SavedSearchType } from "@shared/publicportal/contracts/ISavedSearch";
import CriteriaTable from "./CriteriaTable";
import PlanningApplicationEditSearch from "./editSearch/PlanningApplicationEditSearch";
import EnforcementNoticeEditSearch from "./editSearch/EnforcementNoticeEditSearch";
import SavedSearchService from "@publicPortal/services/SavedSearchService";
import PlanningAppealEditSearch from "./editSearch/PlanningAppealEditSearch";
import _ from "lodash";
import UserCollectedDataHelper, { CriteriaValues } from "../UserCollectedDataHelper";
import EnforcementAppealEditSearch from "./editSearch/EnforcementAppealEditSearch";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/pro-solid-svg-icons";
import "./SavedSearchesTab.css";
import { PublicPortalTheme } from "@publicPortal/theme/MuiPublicPortal.module";
import useAlertService from "@mirage/hooks/useAlertService";
import BuildingRegulationEditSearch from "./editSearch/BuildingRegulationEditSearch";
import PropertySearchEditSearch from "./editSearch/PropertySearchEditSearch";
import { PPConstants } from "@shared/publicportal/constants";
import useSessionStorage from "@mirage/hooks/useSessionStorage";

const SavedSearchesTab = () => {
    const savedSearchesService = new SavedSearchService();
    const InitialOpenModal: OpenEditSearchModal = {
        PlanningApplications: false,
        PlanningAppeals: false,
        EnforcementNotices: false,
        EnforcementAppeals: false,
        BuildingRegulations: false,
        PropertySearch: false,
    };
    const InitialCountSearches: CountSavedSearches = {
        PlanningApplications: 0,
        PlanningAppeals: 0,
        EnforcementNotices: 0,
        EnforcementAppeals: 0,
        BuildingRegulations: 0,
        PropertySearch: 0,
    };
    const [savedSearches, setSavedSearches] = useState<GetSavedSearchResponse[]>([]);
    const [openModal, setOpenModal] = useState<OpenEditSearchModal>(InitialOpenModal);
    const [countSearches, setCountSearches] = useState<CountSavedSearches>(InitialCountSearches);
    const [criteriaToEdit, setCriteriaToEdit] = useState({} as GetSavedSearchResponse);
    const [sessionValue] = useSessionStorage(PPConstants.AppName);
    const showAlert = useAlertService();

    useEffect(() => {
        getSavedSearches();
    }, []);

    useMemo(() => {
        countSavedSearchesPerType();
    }, [savedSearches]);

    const handleCloseModal = () => {
        setOpenModal(InitialOpenModal);
    };

    const handleOpenModal = (modifier: Partial<OpenEditSearchModal>) => {
        setOpenModal((prev) => {
            return {
                ...prev,
                ...modifier,
            };
        });
    };

    return (
        <>
            <PlanningApplicationEditSearch
                criteria={criteriaToEdit}
                isOpen={openModal.PlanningApplications}
                closeModal={handleCloseModal}
                saveEditSearch={async (recordId, searchName, searchType, filter) => await onSaveEditClicked(recordId, searchName, searchType, filter)}
            />
            <EnforcementNoticeEditSearch
                criteria={criteriaToEdit}
                isOpen={openModal.EnforcementNotices}
                closeModal={handleCloseModal}
                saveEditSearch={async (recordId, searchName, searchType, filter) => await onSaveEditClicked(recordId, searchName, searchType, filter)}
            />
            <PlanningAppealEditSearch
                criteria={criteriaToEdit}
                isOpen={openModal.PlanningAppeals}
                closeModal={handleCloseModal}
                saveEditSearch={async (recordId, searchName, searchType, filter) => await onSaveEditClicked(recordId, searchName, searchType, filter)}
            />
            <EnforcementAppealEditSearch
                criteria={criteriaToEdit}
                isOpen={openModal.EnforcementAppeals}
                closeModal={handleCloseModal}
                saveEditSearch={async (recordId, searchName, searchType, filter) => await onSaveEditClicked(recordId, searchName, searchType, filter)}
            />
            <BuildingRegulationEditSearch
                criteria={criteriaToEdit}
                isOpen={openModal.BuildingRegulations}
                closeModal={handleCloseModal}
                saveEditSearch={async (recordId, searchName, searchType, filter) => await onSaveEditClicked(recordId, searchName, searchType, filter)}
            />
            <PropertySearchEditSearch
                criteria={criteriaToEdit}
                isOpen={openModal.PropertySearch}
                closeModal={handleCloseModal}
                saveEditSearch={async (recordId, searchName, searchType, filter) => await onSaveEditClicked(recordId, searchName, searchType, filter)}
            />
            <Accordion defaultExpanded>
                <AccordionSummary
                    expandIcon={<FontAwesomeIcon icon={faChevronDown} style={PublicPortalTheme.icons.sizeS} className="accordion-expand-icon" />}
                    aria-controls="PropertySearch-content"
                    id="PropertySearch-header"
                >
                    <Typography variant="subtitle1">{`Property Search - ${countSearches.PropertySearch}`}</Typography>
                </AccordionSummary>
                <AccordionDetails>{getSearchCriteria("PropertySearch")}</AccordionDetails>
            </Accordion>
            {sessionValue.clientSettings?.BUILDING_REGULATIONS ? (
                <Accordion>
                    <AccordionSummary
                        expandIcon={<FontAwesomeIcon icon={faChevronDown} style={PublicPortalTheme.icons.sizeS} className="accordion-expand-icon" />}
                        aria-controls="Building-Regulations-content"
                        id="Building-Regulations-header"
                    >
                        <Typography variant="subtitle1">{`Building Regulations - ${countSearches.BuildingRegulations}`}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>{getSearchCriteria("BuildingRegulations")}</AccordionDetails>
                </Accordion>
            ) : null}
            {sessionValue.clientSettings?.PLANNING_APPLICATIONS ? (
                <Accordion>
                    <AccordionSummary
                        expandIcon={<FontAwesomeIcon icon={faChevronDown} style={PublicPortalTheme.icons.sizeS} className="accordion-expand-icon" />}
                        aria-controls="Planning-Applications"
                        id="Planning-Applications-header"
                    >
                        <Typography variant="subtitle1">{`Planning Applications - ${countSearches.PlanningApplications}`}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>{getSearchCriteria("PlanningApplications")}</AccordionDetails>
                </Accordion>
            ) : null}
            {sessionValue.clientSettings?.PLANNING_APPEALS ? (
                <Accordion>
                    <AccordionSummary
                        expandIcon={<FontAwesomeIcon icon={faChevronDown} style={PublicPortalTheme.icons.sizeS} className="accordion-expand-icon" />}
                        aria-controls="Planning-Appeals-content"
                        id="Planning-Appeals-header"
                    >
                        <Typography variant="subtitle1">{`Planning Appeals - ${countSearches.PlanningAppeals}`}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>{getSearchCriteria("PlanningAppeals")}</AccordionDetails>
                </Accordion>
            ) : null}
            {sessionValue.clientSettings?.ENFORCEMENT_NOTICES ? (
                <Accordion>
                    <AccordionSummary
                        expandIcon={<FontAwesomeIcon icon={faChevronDown} style={PublicPortalTheme.icons.sizeS} className="accordion-expand-icon" />}
                        aria-controls="Enforcement-Notices-content"
                        id="Enforcement-Notices-header"
                    >
                        <Typography variant="subtitle1">{`Enforcement Notices - ${countSearches.EnforcementNotices}`}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>{getSearchCriteria("EnforcementNotices")}</AccordionDetails>
                </Accordion>
            ) : null}
            {sessionValue.clientSettings?.ENFORCEMENT_APPEALS ? (
                <Accordion>
                    <AccordionSummary
                        expandIcon={<FontAwesomeIcon icon={faChevronDown} style={PublicPortalTheme.icons.sizeS} className="accordion-expand-icon" />}
                        aria-controls="Enforcement-Appeals-content"
                        id="Enforcement-Appeals-header"
                    >
                        <Typography variant="subtitle1">{`Enforcement Appeals - ${countSearches.EnforcementAppeals}`}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>{getSearchCriteria("EnforcementAppeals")}</AccordionDetails>
                </Accordion>
            ) : null}
        </>
    );

    function getSearchCriteria(type: SavedSearchType) {
        return (
            <CriteriaTable
                key={`cri-${type}`}
                criteria={savedSearches.filter((crit) => crit.searchType === type)}
                onEditClicked={onEditClicked}
                onDeleteClicked={async (id) => await onDeleteClicked(id)}
            />
        );
    }

    function countSavedSearchesPerType() {
        setCountSearches(() => {
            return {
                PlanningApplications: savedSearches.filter((crit) => crit.searchType === "PlanningApplications").length,
                PlanningAppeals: savedSearches.filter((crit) => crit.searchType === "PlanningAppeals").length,
                EnforcementNotices: savedSearches.filter((crit) => crit.searchType === "EnforcementNotices").length,
                EnforcementAppeals: savedSearches.filter((crit) => crit.searchType === "EnforcementAppeals").length,
                BuildingRegulations: savedSearches.filter((crit) => crit.searchType === "BuildingRegulations").length,
                PropertySearch: savedSearches.filter((crit) => crit.searchType === "PropertySearch").length,
            };
        });
    }

    async function onSaveEditClicked(recordId: number, searchName: string, savedSearchType: SavedSearchType, filter: CriteriaValues) {
        const saveSearchService = new SavedSearchService();
        handleCloseModal();

        const clonedState = _.clone(filter);
        UserCollectedDataHelper.clearEmptyValues(clonedState);

        const response = await saveSearchService.updateSavedSearch({
            id: recordId,
            name: searchName,
            jsonSearchParameters: JSON.stringify(clonedState),
            searchType: savedSearchType,
        });
        await getSavedSearches();

        if (response.errorMsg) {
            showAlert(response.errorMsg, "error");
        } else {
            showAlert("Search updated successfully", "success");
        }
    }

    async function onDeleteClicked(id: number) {
        const savedSearchService = new SavedSearchService();
        const response = await savedSearchService.deleteSavedSearch({ id });
        await getSavedSearches();

        if (response.errorMsg) {
            showAlert(response.errorMsg, "error");
        } else {
            showAlert("Search deleted successfully!", "success");
        }
    }

    function onEditClicked(criteria: GetSavedSearchResponse) {
        setCriteriaToEdit(criteria);
        const option: Record<SavedSearchType, boolean> = {} as Record<SavedSearchType, boolean>;
        option[criteria.searchType] = true;
        handleOpenModal(option);
    }

    async function getSavedSearches() {
        const response = await savedSearchesService.getSearches();
        if (!Array.isArray(response)) {
            return;
        }
        setSavedSearches(response);
    }
};

type OpenEditSearchModal = {
    [type in SavedSearchType]: boolean;
};

type CountSavedSearches = {
    [type in SavedSearchType]: number;
};

export default SavedSearchesTab;
